import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link as MuiLink,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { LockOutlined, Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PasswordStrengthMeter from '../../components/Auth/PasswordStrengthMeter';
import { evaluatePassword } from '../../utils/passwordStrength';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const isPortal = useMemo(
    () =>
      searchParams.get('portal') === '1' ||
      window.location.pathname.startsWith('/portal/'),
    [searchParams]
  );
  const loginPath = isPortal ? '/portal/login' : '/login';
  const forgotPath = isPortal ? '/portal/forgot-password' : '/forgot-password';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const strength = evaluatePassword(password);
  const canSubmit =
    Boolean(token) &&
    strength.valid &&
    password === confirmPassword &&
    !isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Reset link is missing or invalid. Request a new one.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!strength.valid) {
      setError(strength.errors.join('. ') || 'Password does not meet requirements');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json.message || 'Unable to reset password');
      }
      setDone(true);
      toast.success('Password reset successfully');
    } catch (err: any) {
      setError(err.message || 'Unable to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2,
      }}
    >
      <Paper elevation={8} sx={{ width: '100%', maxWidth: 440, borderRadius: 3, overflow: 'hidden' }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            padding: 3,
            textAlign: 'center',
          }}
        >
          <LockOutlined sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h5" fontWeight={600}>
            Reset password
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            Choose a new password for your account
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {!token && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This reset link is missing a token. Please{' '}
              <MuiLink component={Link} to={forgotPath}>
                request a new link
              </MuiLink>
              .
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {done ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your password has been updated. You can sign in with your new password.
              </Alert>
              <Button fullWidth variant="contained" onClick={() => navigate(loginPath)}>
                Go to {isPortal ? 'portal' : ''} login
              </Button>
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                required
                type={showPassword ? 'text' : 'password'}
                label="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading || !token}
                sx={{ mb: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <PasswordStrengthMeter password={password} />
              <TextField
                fullWidth
                required
                type={showPassword ? 'text' : 'password'}
                label="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading || !token}
                sx={{ mt: 2, mb: 2 }}
                error={Boolean(confirmPassword) && confirmPassword !== password}
                helperText={
                  confirmPassword && confirmPassword !== password ? 'Passwords do not match' : ' '
                }
              />
              <Button type="submit" fullWidth variant="contained" disabled={!canSubmit} sx={{ mb: 2 }}>
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Update password'}
              </Button>
              <Box sx={{ textAlign: 'center' }}>
                <MuiLink component={Link} to={loginPath} underline="hover">
                  <ArrowBack sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                  Back to login
                </MuiLink>
              </Box>
            </form>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ResetPasswordPage;
